import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost } from '@claritio/frontend/lib/api'
import { useInterval } from '@claritio/frontend/lib/useInterval'
import { toast } from '@claritio/frontend/components/ui/toast'
import type { AppNotification } from '@claritio/frontend/types'
import i18n from 'i18next'

interface PollResponse { unread_count: number; toasts: AppNotification[] }
interface IndexResponse { notifications: AppNotification[]; unread_count: number }

export interface NotificationsApi {
  unread: number
  items: AppNotification[]
  loadList: () => Promise<void>
  openItem: (n: AppNotification) => Promise<void>
  markAll: () => Promise<void>
}

// ENG-108 polling + state, lifted out of the bell (ENG-129): the bell renders in
// BOTH sidebar instances (desktop aside + mobile drawer), and each instance
// polling on its own would double every request and toast. Call this ONCE in
// AppShell and hand the result to every bell.
export function useNotifications(): NotificationsApi {
  const navigate = useNavigate()
  const [unread, setUnread] = useState(0)
  const [items, setItems] = useState<AppNotification[]>([])

  // Generation links point into RequireOrg routes (ENG-113) — carry the
  // notification's org along or the click bounces to /clients.
  const go = useCallback((n: AppNotification) => {
    if (!n.link) return
    const needsOrg = n.organization_id && !/[?&]org_id=/.test(n.link)
    navigate(needsOrg ? `${n.link}${n.link.includes('?') ? '&' : '?'}org_id=${n.organization_id}` : n.link)
  }, [navigate])

  const poll = useCallback(async () => {
    try {
      const res = await apiPost<PollResponse>('/api/v1/notifications/poll')
      setUnread(res.unread_count)
      res.toasts.forEach((n) => toast(n.title, { tone: n.status === 'error' ? 'error' : 'success', onClick: () => go(n) }))
    } catch { /* transient poll error — ignore, next tick retries */ }
  }, [go])

  // setUnread runs after an await inside poll(); see the note in lib/auth.tsx.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { poll() }, [poll]) // on load: badge + any pending toasts (return/refresh)
  useInterval(poll, 20_000)

  const loadList = useCallback(async () => {
    try {
      const res = await apiGet<IndexResponse>('/api/v1/notifications')
      setItems(res.notifications)
      setUnread(res.unread_count)
    } catch { /* ignore */ }
  }, [])

  const openItem = useCallback(async (n: AppNotification) => {
    if (!n.read_at) { try { await apiPost(`/api/v1/notifications/${n.id}/read`) } catch { /* ignore */ } }
    go(n)
    poll()
  }, [go, poll])

  // Only marks them read here once the server has; a failure leaves the list as it was.
  const markAll = useCallback(async () => {
    try {
      await apiPost('/api/v1/notifications/read_all')
    } catch {
      toast(i18n.t('common:action.failed'), { tone: 'error' })
      return
    }
    setUnread(0)
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? new Date().toISOString() })))
  }, [])

  return { unread, items, loadList, openItem, markAll }
}
